import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Loader2, Gift } from "lucide-react";

export default function AuthPage({ mode = "login" }) {
  const isLogin = mode === "login";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refParam = searchParams.get("ref");
  const [refCode, setRefCode] = useState(refParam || localStorage.getItem("autosaas_ref") || "");

  useEffect(() => {
    if (refParam) {
      localStorage.setItem("autosaas_ref", refParam.toUpperCase());
      setRefCode(refParam.toUpperCase());
    }
  }, [refParam]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Bienvenue !");
      } else {
        await register(name, email, password);
        toast.success("Compte créé avec succès !");
      }
      navigate("/tableau-de-bord");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur, réessayez");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="w-full max-w-md relative animate-fade-up">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
            <Sparkles size={18} className="text-white" />
          </div>
          <span className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>AutoSaaS<span className="text-indigo-400">.ai</span></span>
        </Link>

        <div className="glass rounded-2xl p-8">
          <h1 className="text-2xl font-bold" data-testid="auth-title">
            {isLogin ? "Content de vous revoir" : "Créez votre compte"}
          </h1>
          <p className="text-sm text-gray-400 mt-2">
            {isLogin ? "Connectez-vous pour continuer à générer." : "Gratuit — sans carte de crédit."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {!isLogin && refCode && (
              <div className="flex items-center gap-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 px-4 py-3 text-sm" data-testid="referral-banner">
                <Gift size={16} className="text-emerald-400 shrink-0" />
                <span className="text-emerald-200">Vous êtes parrainé·e — code <span className="font-mono font-semibold">{refCode}</span></span>
              </div>
            )}
            {!isLogin && (
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-gray-400">Prénom</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 outline-none focus:border-indigo-500 focus:bg-white/[0.07] transition-colors"
                  placeholder="Camille"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-gray-400">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 outline-none focus:border-indigo-500 focus:bg-white/[0.07] transition-colors"
                placeholder="vous@exemple.com"
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-gray-400">Mot de passe</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-2 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 outline-none focus:border-indigo-500 focus:bg-white/[0.07] transition-colors"
                placeholder="Minimum 6 caractères"
                data-testid="auth-password-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="pill-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="auth-submit-button"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {isLogin ? "Se connecter" : "Créer mon compte"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-400">
            {isLogin ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
            <Link
              to={isLogin ? "/inscription" : "/connexion"}
              className="text-indigo-400 hover:text-indigo-300 font-medium"
              data-testid="auth-switch-link"
            >
              {isLogin ? "Créer un compte" : "Se connecter"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
